import { computed, ref } from 'vue';
import { useRouter } from 'vue-router';

import type { TipTapContent } from '@/components/canvas/docxContent';
import { useChatSessionsStore } from '@/stores/chatSessions';
import { useDocumentsStore } from '@/stores/documentsStore';
import { usePptxStore } from '@/stores/pptxStore';
import { useTenantStore } from '@/stores/tenant';
import type { ChatSession, DocumentSession } from '@/types';

export type LinkableDocumentType = 'pptx' | 'slides' | 'doc';

/** Whether cleanup should also archive the pending presentation chat session. */
export type DocumentSessionCleanupScope = 'document' | 'session';

export const CHAT_SESSION_ALREADY_LINKED_ERROR =
    'Chat session already has a linked document';

type CreateDocumentSessionOptions = {
    /** Reuse the in-flight chat session instead of creating a new one. */
    usePendingChatSession?: boolean;
    linkToChatSessionId?: string;
    /** Message the document originates from, when created from a chat answer. */
    referrerId?: string;
    /** Initial TipTap content, for 'doc' documents. */
    docContent?: TipTapContent;
    /** Groups the created session under a project, like a plain chat started from one. */
    projectId?: string;
};

export function useDocumentSession() {
    const router = useRouter();
    const tenantStore = useTenantStore();
    const chatSessionsStore = useChatSessionsStore();
    const documentsStore = useDocumentsStore();
    const pptxStore = usePptxStore();

    const tenantId = computed(() => tenantStore.tenantId);
    const pendingDocumentId = ref<string | null>(null);

    async function createDocumentSession(
        documentType: LinkableDocumentType,
        name: string,
        options: CreateDocumentSessionOptions = {}
    ): Promise<DocumentSession> {
        if (!tenantId.value) {
            throw new Error('Tenant ID is required');
        }

        let chatSession: ChatSession;
        const usePendingChatSession = options.usePendingChatSession === true;

        if (options.linkToChatSessionId) {
            const requestedSession = chatSessionsStore.chatSessions.find(
                (session) => session.id === options.linkToChatSessionId
            );
            if (!requestedSession) {
                throw new Error('Chat session not found');
            }
            if (
                requestedSession.document_id &&
                requestedSession.document_id !== 'pending'
            ) {
                throw new Error(CHAT_SESSION_ALREADY_LINKED_ERROR);
            }
            chatSession = requestedSession;
        } else if (usePendingChatSession && pptxStore.pendingChatSessionId) {
            const existingSession = chatSessionsStore.chatSessions.find(
                (session) => session.id === pptxStore.pendingChatSessionId
            );

            if (!existingSession) {
                throw new Error('Chat session not found');
            }
            chatSession = existingSession;
        } else {
            chatSession = await chatSessionsStore.addChatSession({
                name,
                tenantId: tenantId.value,
                documentId: 'pending',
                projectId: options.projectId
            });
            if (!chatSession) {
                throw new Error('Failed to create chat session');
            }
            pptxStore.setPendingChatSessionId(chatSession.id);
            await chatSessionsStore.markChatSessionAsVisited(chatSession.id);
        }
        const documentName = options.linkToChatSessionId
            ? name
            : chatSession.name;

        const document = await documentsStore.addDocument(
            documentName,
            tenantId.value,
            documentType,
            undefined,
            options.referrerId,
            chatSession.id,
            options.docContent
        );

        if (!document) {
            throw new Error('Failed to create document');
        }
        pendingDocumentId.value = document.id;

        await chatSessionsStore.updateChatSession(
            chatSession.id,
            undefined,
            document.id
        );

        return { chatSession, document };
    }

    /**
     * Return the chat session paired with an existing document, creating it on
     * the fly for documents that don't have one yet. `projectId` groups the one it creates.
     */
    async function ensureChatSessionForDocument(
        document: {
            id: string;
            name: string;
        },
        projectId?: string
    ): Promise<ChatSession> {
        if (!tenantId.value) {
            throw new Error('Tenant ID is required');
        }

        const existingSession = chatSessionsStore.chatSessions.find(
            (session) => session.document_id === document.id
        );
        if (existingSession) {
            return existingSession;
        }

        const chatSession = await chatSessionsStore.addChatSession({
            name: document.name,
            tenantId: tenantId.value,
            documentId: document.id,
            projectId
        });
        if (!chatSession) {
            throw new Error('Failed to create chat session');
        }

        return chatSession;
    }

    function redirectToViewer(
        chatId: string,
        documentId: string,
        documentType: LinkableDocumentType,
        composerText?: string
    ) {
        chatSessionsStore.shouldScrollToTop = true;
        /* Returned so callers can hold their UI until the viewer is mounted. */
        return router.push({
            path: '/viewer',
            query: {
                chatId,
                fileId: documentId,
                fileType: documentType
            },
            ...(composerText ? { state: { composerText } } : {})
        });
    }

    /**
     * Text passed via router state when opening the viewer, taken once.
     *
     * Reading it clears it, so the callers that ask on mount and on every session change cannot
     * both insert it. `history.state` is `null` until something writes one, and the assertion
     * would hide that from the compiler.
     */
    function consumeViewerComposerText(): string | undefined {
        const state = (history.state ?? {}) as { composerText?: string };
        const text = state.composerText?.trim();
        if (!text) return undefined;
        history.replaceState({ ...state, composerText: undefined }, '');
        return text;
    }

    async function cleanupOrphanedResources(
        scope: DocumentSessionCleanupScope = 'document'
    ) {
        try {
            if (pendingDocumentId.value && tenantId.value) {
                await documentsStore.archiveDocument(
                    pendingDocumentId.value,
                    tenantId.value
                );
            }

            if (
                scope === 'session' &&
                pptxStore.pendingChatSessionId &&
                tenantId.value
            ) {
                await chatSessionsStore.archiveChatSession(
                    pptxStore.pendingChatSessionId,
                    tenantId.value
                );
            }
        } catch (error) {
            console.error('Error cleaning up orphaned resources:', error);
        } finally {
            resetPendingCreationState(scope);
        }
    }

    function resetPendingCreationState(
        scope: DocumentSessionCleanupScope = 'document'
    ) {
        pendingDocumentId.value = null;
        if (scope === 'session') {
            pptxStore.resetPendingChatSessionId();
        }
    }

    function registerPendingDocument(documentId: string) {
        pendingDocumentId.value = documentId;
    }

    return {
        createDocumentSession,
        ensureChatSessionForDocument,
        cleanupOrphanedResources,
        redirectToViewer,
        consumeViewerComposerText,
        resetPendingCreationState,
        registerPendingDocument
    };
}
